// Money helpers for the purchase-credits card. Separated from the component so
// the parsing rule below can be tested without rendering anything — it is the
// one place a user's typing turns into an amount we will charge them.

/** Cents as US dollars, dropping a trailing `.00` — `$50`, `$12.50`. */
export function formatCredits(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

/**
 * A typed dollar amount as whole cents, or null when it isn't one.
 *
 * Rounds rather than truncates, so `12.999` is $13.00 and not $12.99 — but the
 * bounds are checked by the caller against the returned cents, and again by
 * the server, so rounding can never carry an amount past a limit unchecked.
 * Rejects anything non-finite or non-positive rather than coercing it: `NaN`
 * silently becoming 0 is how a "free" top-up button happens.
 */
export function parseCreditAmount(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, '').replace(/,/g, '')
  if (!trimmed) return null
  const dollars = Number(trimmed)
  if (!Number.isFinite(dollars) || dollars <= 0) return null
  return Math.round(dollars * 100)
}
